import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { SearchFieldComponent } from '../../shared/components/search-field/search-field.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { WebsitesService } from '../../core/services/websites.service';
import {
  catchError,
  combineLatest,
  filter,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  take,
} from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { toObservable } from '@angular/core/rxjs-interop';
import { Website } from '../../shared/models/website.models';
import { TableComponent } from '../../shared/components/table/table.component';
import { ServersService } from '../../core/services/servers.service';
import { PageEvent } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { MatRippleModule } from '@angular/material/core';
import { WebsiteGroupsService } from '../../core/services/website-groups.service';
import { PlansService } from '../../core/services/plans.service';
import { WebsiteGroupRecord } from '../../shared/models/website-group.models';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { AddWebsiteDialogComponent } from './dialogs/add-website-dialog/add-website-dialog.component';
import { FormControl } from '@angular/forms';
import {
  CreateWebsiteDTO,
  CreateWebsiteAliasDTO,
  CreateWebsiteSubdomainDTO,
  WebsiteEnvironment,
} from '../../shared/models/website-create.models';
import { TemporaryWPCredentials } from '../../shared/models/wordpress.models';
import { WPCliService } from '../../core/services/wp-cli.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackbarComponent } from '../../shared/components/snackbar/snackbar.component';
import { RouterLink } from '@angular/router';
import { StatusLabelComponent } from '../../shared/components/status-label/status-label.component';

@Component({
  selector: 'app-dashboard',
  imports: [
    SearchFieldComponent,
    FontAwesomeModule,
    MatButtonModule,
    MatCardModule,
    AsyncPipe,
    TableComponent,
    MatTableModule,
    MatRippleModule,
    MatTooltipModule,
    RouterLink,
    StatusLabelComponent,
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardComponent {
  private websitesService = inject(WebsitesService);

  private serversService = inject(ServersService);

  private websiteGroupsService = inject(WebsiteGroupsService);

  private plansService = inject(PlansService);

  private wpCliService = inject(WPCliService);

  readonly dialog = inject(MatDialog);

  readonly snackbar = inject(MatSnackBar);

  plans$ = this.plansService.getAll().pipe(shareReplay(1));

  servers$ = this.serversService.getAllServers().pipe(
    catchError(() => of([])),
    shareReplay(1),
  );

  types: Record<string, string> = {
    MAIN: 'Main',
    SUB_WEBSITE: 'Site',
    ALIAS: 'Alias',
    STAGING: 'Staging',
  };

  // Websites

  filters = signal<{ page: number; domainSearch?: string }>({ page: 1 });

  filters$ = toObservable(this.filters);

  searchControl = new FormControl('');

  totalWebsites = signal(0);

  websites$ = this.filters$.pipe(
    switchMap((filters) => this.websitesService.searchWebsites(filters)),
    switchMap(({ count, Websites }) => {
      this.totalWebsites.set(count);
      return combineLatest({
        websites: of(Websites),
        servers: this.servers$,
      });
    }),
    map(({ websites, servers }) =>
      websites.map((w) => ({
        ...w,
        originServer: servers.find((s) => s._id === w.originServer)?.hostname || 'N/A',
      })),
    ),
  );

  websiteColumns = [
    {
      columnDef: 'id',
      header: 'ID',
      cell: (element: Website) => `${element.id}`,
    },
    {
      columnDef: 'type',
      header: 'Type',
      cell: (element: Website) => `${this.types[element.type] || 'N/A'}`,
    },
    {
      columnDef: 'website',
      header: 'Website',
      cell: (element: Website) => `${element.domain}`,
    },
    {
      columnDef: 'status',
      header: 'Status',
      cell: (element: Website) => `${element.status}`,
      useTemplate: true,
    },
    {
      columnDef: 'server',
      header: 'Server',
      cell: (element: Website) => `${element.originServer}`,
    },
    {
      columnDef: 'buttons',
      header: '',
      cell: () => '',
      useTemplate: true,
    },
  ];

  // Groups

  groupsFilters = signal<{ page: number; owner?: string; itemsPerPage: number; domain?: string }>({
    page: 1,
    itemsPerPage: 10,
  });

  groupsFilters$ = toObservable(this.groupsFilters);

  totalGroups = signal(0);

  groups$ = this.groupsFilters$.pipe(
    switchMap((filters) => this.websiteGroupsService.searchGroups(filters)),
    map((websiteGroups) => ({
      websiteGroups,
      websiteIds: websiteGroups.groups.map((group) => group.mainWebsite),
    })),
    switchMap(({ websiteGroups, websiteIds }) =>
      combineLatest({
        websites: this.websitesService.getWebsitesByIds(websiteIds).pipe(map((w) => w.Websites)),
        websiteGroups: of(websiteGroups),
        plans: this.plans$,
        servers: this.servers$,
      }),
    ),
    map(({ websites, websiteGroups, plans, servers }) => {
      this.totalGroups.set(websiteGroups.count);

      return websiteGroups.groups.map((group) => {
        const groupWebsite = websites.find((website) => website.id === group.mainWebsite);
        const server = servers.find((s) => s._id === groupWebsite?.originServer);
        const plan = plans[group.plan!];

        return {
          id: group.id,
          plan: plan,
          website: groupWebsite!,
          owner: group.owner,
          websiteCount: group.websiteCount,
          server: server,
        };
      });
    }),
  );

  groupsColumns = [
    {
      columnDef: 'id',
      header: 'ID',
      cell: (element: WebsiteGroupRecord) => `${element.id}`,
    },
    {
      columnDef: 'plan',
      header: 'Plan',
      cell: (element: WebsiteGroupRecord) => `${element.plan?.name || '-'}`,
    },
    {
      columnDef: 'website',
      header: 'Website',
      cell: (element: WebsiteGroupRecord) => `${element.website?.domain || '-'}`,
    },
    {
      columnDef: 'status',
      header: 'Status',
      cell: (element: WebsiteGroupRecord) => `${element.website?.status || '-'}`,
      useTemplate: true,
    },
    {
      columnDef: 'server',
      header: 'Server',
      cell: (element: WebsiteGroupRecord) => `${element.server?.hostname || '-'}`,
    },
    {
      columnDef: 'websitesCount',
      header: 'Used Sites',
      cell: (element: WebsiteGroupRecord) =>
        `${element.websiteCount + ' / ' + element.plan?.websitesCount || '-'}`,
    },
    {
      columnDef: 'buttons',
      header: '',
      cell: () => '',
      useTemplate: true,
    },
  ];

  handleWPageEvent(event: PageEvent) {
    this.filters.update((f) => ({ ...f, page: event.pageIndex + 1 }));
  }

  handleGPageEvent(event: PageEvent) {
    this.groupsFilters.update((f) => ({ ...f, page: event.pageIndex + 1 }));
  }

  onSearch(text: string) {
    this.filters.update((f) => ({ ...f, domainSearch: text }));
    this.groupsFilters.update((f) => ({ ...f, domain: text }));
  }

  addWebsite(): void {
    combineLatest({ servers: this.servers$, plans: this.plans$ })
      .pipe(
        map(({ servers, plans }) => ({
          servers: servers.map((s) => ({ label: s.hostname, value: s._id })),
          plans: plans.map((p) => ({ label: p.name, value: p.id })),
        })),
        switchMap(({ servers, plans }) =>
          this.dialog
            .open(AddWebsiteDialogComponent, {
              data: { servers, plans },
            })
            .afterClosed(),
        ),
        take(1),
        filter((x) => !!x),
        switchMap(
          (result: {
            website: CreateWebsiteDTO | CreateWebsiteAliasDTO | CreateWebsiteSubdomainDTO;
            wordpress?: TemporaryWPCredentials;
          }) => {
            const create$ = result.wordpress
              ? this.wpCliService
                  .saveTempCredentials(result.wordpress)
                  .pipe(switchMap(() => this.createWebsite(result.website)))
              : this.createWebsite(result.website);

            return create$.pipe(
              map((createResult) => ({
                createResult,
                website: result.website,
              })),
            );
          },
        ),
      )
      .subscribe({
        next: ({ website }) =>
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `Adding website ${website.domain} started`,
              highlights: [website.domain],
            },
          }),
        error: (error) =>
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: error.error?.error, // To be checked
            },
          }),
      });
  }

  private createWebsite(
    website: CreateWebsiteDTO | CreateWebsiteAliasDTO | CreateWebsiteSubdomainDTO,
  ): Observable<Website> {
    if (this.isAlias(website)) {
      return this.websitesService.createWebsiteAlias(website);
    } else if (this.isSubdomain(website)) return this.websitesService.createSubwebsite(website);
    else return this.websitesService.createWebsite(website);
  }

  private isAlias(
    website: CreateWebsiteDTO | CreateWebsiteAliasDTO | CreateWebsiteSubdomainDTO,
  ): website is CreateWebsiteAliasDTO {
    return (website as CreateWebsiteAliasDTO).websiteEnvironment === WebsiteEnvironment.ALIAS;
  }

  private isSubdomain(
    website: CreateWebsiteDTO | CreateWebsiteAliasDTO | CreateWebsiteSubdomainDTO,
  ): website is CreateWebsiteSubdomainDTO {
    return (website as CreateWebsiteAliasDTO).websiteEnvironment === WebsiteEnvironment.SUB;
  }
}
